import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Button,
  Input,
  Card,
  CardContent,
  Alert,
  AlertDescription,
  VirtualizedMultiSelectCombobox,
  RadioGroup,
  RadioGroupItem,
} from '@roger/ui-components';
import { Search, Loader2, AlertCircle, Upload, Archive, X } from 'lucide-react';
import { CODING_VISIT_TYPE_OPTIONS } from '../constants/codingVisitTypes';
import { CodingTags, CodingTagTypes } from '../types/codingData.types';

// Coding status filter options
const CODING_STATUS_OPTIONS = [
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Action Required' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
];

// Reusable TagFilter component
interface TagFilterProps {
  tagKey: CodingTagTypes;
  label: string;
  icon: React.ReactNode;
  value: string;
  onValueChange: (value: string) => void;
  onClear: () => void;
  showClear: boolean;
  disabled: boolean;
}

const TagFilter: React.FC<TagFilterProps> = ({
  tagKey,
  label,
  icon,
  value,
  onValueChange,
  onClear,
  showClear,
  disabled,
}) => (
  <div className='flex items-center bg-gray-50 border border-gray-200 rounded-lg'>
    <div className='flex items-center gap-2 px-3 py-2 border-r border-gray-200'>
      {icon}
      <span className='text-sm text-gray-700'>{label}</span>
    </div>
    <div className='flex items-center gap-3 px-3 py-2'>
      <RadioGroup
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
        className='flex flex-row gap-3'
      >
        <div className='flex items-center gap-1.5'>
          <RadioGroupItem value='true' id={`${tagKey}_yes`} />
          <label
            htmlFor={`${tagKey}_yes`}
            className='text-sm text-gray-600 cursor-pointer'
          >
            Yes
          </label>
        </div>
        <div className='flex items-center gap-1.5'>
          <RadioGroupItem value='false' id={`${tagKey}_no`} />
          <label
            htmlFor={`${tagKey}_no`}
            className='text-sm text-gray-600 cursor-pointer'
          >
            No
          </label>
        </div>
      </RadioGroup>
      {showClear && (
        <button
          type='button'
          onClick={onClear}
          disabled={disabled}
          className='w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 hover:text-gray-700 ml-1'
        >
          <X className='w-3 h-3' />
        </button>
      )}
    </div>
  </div>
);

// Form validation schema
const codingSearchSchema = z
  .object({
    phrase: z.string(),
    start_date: z.string().nullable(),
    end_date: z.string().nullable(),
    coding_status: z.array(z.string()),
    visit_types: z.array(z.string()),
    clinics: z.array(z.string()),
    tags: z
      .object({
        EMR_filled: z.boolean().optional(),
        archived: z.boolean().optional(),
      })
      .optional(),
  })
  .refine(
    data => {
      if (data.phrase.trim() && data.phrase.trim().length < 3) {
        return false;
      }
      return true;
    },
    {
      message: 'Search phrase must be at least 3 characters long',
      path: ['phrase'],
    }
  )
  .refine(
    data => {
      if (data.start_date) {
        const startDate = new Date(data.start_date);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (startDate > today) {
          return false;
        }
      }
      return true;
    },
    {
      message: 'Start date cannot be in the future',
      path: ['start_date'],
    }
  )
  .refine(
    data => {
      if (data.start_date && data.end_date) {
        const startDate = new Date(data.start_date);
        const endDate = new Date(data.end_date);
        if (endDate < startDate) {
          return false;
        }
      }
      return true;
    },
    {
      message: 'End date cannot be before start date',
      path: ['end_date'],
    }
  );

export type CodingSearchFormValues = z.infer<typeof codingSearchSchema>;

export interface CodingSearchParams {
  phrase: string;
  start_date: Date | null;
  end_date: Date | null;
  coding_status: string[];
  visit_types: string[];
  clinics: string[];
  tags?: CodingTags;
}

interface CodingSearchFiltersProps {
  defaultValues: CodingSearchParams;
  onSearch: (params: CodingSearchParams) => void;
  onClear: () => void;
  searching: boolean;
  loading: boolean;
  clinicOptions: Array<{ value: string; label: string }>;
}

// Helper to convert Date to string for form
const dateToString = (date: Date | null): string => {
  if (!date) return '';
  return date.toISOString().split('T')[0];
};

// Helper to convert string to Date
const stringToDate = (str: string | null): Date | null => {
  if (!str) return null;
  return new Date(str);
};

export const CodingSearchFilters: React.FC<CodingSearchFiltersProps> = ({
  defaultValues,
  onSearch,
  onClear,
  searching,
  loading,
  clinicOptions,
}) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CodingSearchFormValues>({
    resolver: zodResolver(codingSearchSchema),
    defaultValues: {
      phrase: defaultValues.phrase,
      start_date: dateToString(defaultValues.start_date),
      end_date: dateToString(defaultValues.end_date),
      coding_status: defaultValues.coding_status,
      visit_types: defaultValues.visit_types,
      clinics: defaultValues.clinics,
      tags: defaultValues.tags,
    },
  });

  // Reset form when defaultValues change (e.g., on clear)
  useEffect(() => {
    reset({
      phrase: defaultValues.phrase,
      start_date: dateToString(defaultValues.start_date),
      end_date: dateToString(defaultValues.end_date),
      coding_status: defaultValues.coding_status,
      visit_types: defaultValues.visit_types,
      clinics: defaultValues.clinics,
      tags: defaultValues.tags,
    });
  }, [defaultValues, reset]);

  // Watch tags for radio button state
  const watchedTags = watch('tags');

  const onSubmit = (data: CodingSearchFormValues) => {
    // Clean tags object - only include keys that have boolean values
    const cleanedTags: CodingTags = {};
    if (data.tags?.EMR_filled !== undefined) {
      cleanedTags.EMR_filled = data.tags.EMR_filled;
    }
    if (data.tags?.archived !== undefined) {
      cleanedTags.archived = data.tags.archived;
    }

    onSearch({
      phrase: data.phrase,
      start_date: stringToDate(data.start_date),
      end_date: stringToDate(data.end_date),
      coding_status: data.coding_status,
      visit_types: data.visit_types,
      clinics: data.clinics,
      tags: Object.keys(cleanedTags).length > 0 ? cleanedTags : undefined,
    });
  };

  // Handler for tag radio buttons
  const handleTagChange = (
    tagKey: CodingTagTypes,
    value: boolean | undefined
  ) => {
    const currentTags = watchedTags || {};
    if (value === undefined) {
      // Remove the key from tags
      const { [tagKey]: _removed, ...rest } = currentTags;
      void _removed; // Explicitly mark as intentionally unused
      setValue('tags', Object.keys(rest).length > 0 ? rest : undefined);
    } else {
      setValue('tags', { ...currentTags, [tagKey]: value });
    }
  };

  // Check if tag has no selection (neither Yes nor No)
  const isTagUnselected = (tagKey: CodingTagTypes): boolean => {
    return watchedTags?.[tagKey] === undefined;
  };

  // Get RadioGroup value from tag state (converts boolean to string)
  // Returns empty string when unset to properly clear RadioGroup selection
  const getTagRadioValue = (tagKey: CodingTagTypes): string => {
    const value = watchedTags?.[tagKey];
    if (value === true) return 'true';
    if (value === false) return 'false';
    return '';
  };

  // Handle RadioGroup value change (converts string to boolean)
  const handleTagRadioChange = (tagKey: CodingTagTypes, value: string) => {
    const boolValue = value === 'true';
    handleTagChange(tagKey, boolValue);
  };

  const handleClear = () => {
    onClear();
  };

  // Get first error message for display
  const getErrorMessage = (): string | null => {
    if (errors.phrase?.message) return errors.phrase.message;
    if (errors.start_date?.message) return errors.start_date.message;
    if (errors.end_date?.message) return errors.end_date.message;
    return null;
  };

  const errorMessage = getErrorMessage();

  return (
    <Card className='mb-6'>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Search Bar Row */}
          <div className='flex gap-3 items-center'>
            <div className='relative flex-1'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
              <Input
                placeholder='Search by patient name, MRN, primary diagnosis...'
                {...register('phrase')}
                disabled={loading}
                className='pl-10'
              />
            </div>
            <Button
              type='submit'
              disabled={searching || loading}
              className='bg-blue-600 hover:bg-blue-700 text-white'
            >
              {searching ? (
                <Loader2 className='w-4 h-4 animate-spin' />
              ) : (
                <Search className='w-4 h-4' />
              )}
              Search
            </Button>
            <Button
              type='button'
              variant='ghost'
              onClick={handleClear}
              disabled={loading}
              className='text-gray-500 hover:text-gray-700'
            >
              Clear
            </Button>
          </div>

          {/* Filters Row */}
          <div className='grid grid-cols-2 md:grid-cols-5 gap-3 mt-4'>
            <div className='space-y-1'>
              <label className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
                From
              </label>
              <Input
                type='date'
                {...register('start_date')}
                disabled={loading}
              />
            </div>

            <div className='space-y-1'>
              <label className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
                To
              </label>
              <Input type='date' {...register('end_date')} disabled={loading} />
            </div>

            <div className='space-y-1'>
              <label className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
                Visit Type
              </label>
              <Controller
                name='visit_types'
                control={control}
                render={({ field }) => (
                  <VirtualizedMultiSelectCombobox
                    options={CODING_VISIT_TYPE_OPTIONS}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder='All types'
                    searchPlaceholder='Search visit types...'
                    disabled={loading}
                    maxDisplayItems={1}
                  />
                )}
              />
            </div>

            <div className='space-y-1'>
              <label className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
                Clinic
              </label>
              <Controller
                name='clinics'
                control={control}
                render={({ field }) => (
                  <VirtualizedMultiSelectCombobox
                    options={clinicOptions}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder='All clinics'
                    searchPlaceholder='Search clinics...'
                    disabled={loading || clinicOptions.length === 0}
                    maxDisplayItems={1}
                    popoverWidth='350px'
                  />
                )}
              />
            </div>

            <div className='space-y-1'>
              <label className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
                Status
              </label>
              <Controller
                name='coding_status'
                control={control}
                render={({ field }) => (
                  <VirtualizedMultiSelectCombobox
                    options={CODING_STATUS_OPTIONS}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder='All statuses'
                    searchPlaceholder='Search statuses...'
                    disabled={loading}
                    maxDisplayItems={1}
                  />
                )}
              />
            </div>
          </div>

          {/* Tags Row */}
          <div className='flex items-center gap-4 mt-4'>
            <label className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
              Tags
            </label>

            <TagFilter
              tagKey='EMR_filled'
              label='Filled into EMR'
              icon={<Upload className='w-4 h-4 text-green-600' />}
              value={getTagRadioValue('EMR_filled')}
              onValueChange={value => handleTagRadioChange('EMR_filled', value)}
              onClear={() => handleTagChange('EMR_filled', undefined)}
              showClear={!isTagUnselected('EMR_filled')}
              disabled={loading}
            />

            <TagFilter
              tagKey='archived'
              label='Archived'
              icon={<Archive className='w-4 h-4 text-orange-600' />}
              value={getTagRadioValue('archived')}
              onValueChange={value => handleTagRadioChange('archived', value)}
              onClear={() => handleTagChange('archived', undefined)}
              showClear={!isTagUnselected('archived')}
              disabled={loading}
            />
          </div>

          {/* Validation Error Alert */}
          {errorMessage && (
            <Alert className='border-red-200 bg-red-50 mt-3'>
              <AlertDescription className='text-red-800 flex items-center gap-2'>
                <AlertCircle className='h-4 w-4 text-red-600' />
                {errorMessage}
              </AlertDescription>
            </Alert>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

export default CodingSearchFilters;
